import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from 'react-bootstrap';
import Header from "../../Components/Header/Header";
import SignUpTitle from "../../Components/SignUpTitle/SignUpTitle";
import CheckoutForm from "../../Components/CheckoutForm/CheckoutForm";
import Buttons from "../../Components/Buttons/Buttons";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import "./PaymentPage.css";
import PopCheck from '../../Images/PopCheck.png';
import Popup from '../../Components/Popup/Popup';
import { useGlobalState } from '../../Contexts/GlobalStateContext';
import { useApi } from "../../Services/api";
import RatesTable from '../../Components/RatesTable/RatesTable'; 

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const PaymentPage = () => {
  const [cardholderName, setCardholderName] = useState("");
  const [creditCardValid, setCreditCardValid] = useState(true);
  const [creditCardError, setCreditCardError] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [options, setOptions] = useState({});
  const [paymentInfo, setPaymentInfo] = useState({
    paymentMethod: '',
    displayBrand: '',
    expMonth: '',
    expYear: '',
    funding: '',
    last4: ''
  });
  const checkoutFormRef = useRef();
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  const { tempClientId, setErrorVisible, setErrorMessage, setVoyceToken, setTempClientId, setRemember } = useGlobalState();
  const api = useApi();

  const paymentDataF = async (newPaymentInfo) => {
    const paymentBody = {
      TempClientId: tempClientId,
      payment: {
        TempClientId: tempClientId,
        CardHolderName: cardholderName,
        StripeCustomerId: paymentInfo.stripeCustomerId,
        IntentId: paymentInfo.intentId,
        PaymentMethod: newPaymentInfo.paymentMethod,
        DisplayBrand: newPaymentInfo.displayBrand,
        ExpMonth: newPaymentInfo.expMonth,
        ExpYear: newPaymentInfo.expYear,
        Funding: newPaymentInfo.funding,
        Last4: newPaymentInfo.last4,
      }
    };
    try {
      const raw = await api.post(`Client/Create/Step/${3}`, paymentBody);
      const paymentData = raw.data;
      if (!paymentData.Success) {
        setErrorVisible(true);
        setErrorMessage(paymentData.Error);
        return;
      }
      setRemember(true);
      setVoyceToken(paymentData.Token);
      setTempClientId(null);
      setShowSuccessPopup(true);
    } catch (error) {
      console.log('error:', error);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (!checkoutFormRef.current) return;
    let ref;
    try {
      console.log('checkoutFormRef:', checkoutFormRef);
      ref = await checkoutFormRef.current.handleCreditSubmit();
      console.log('ref:', ref);
    } catch (error) {
      console.log('error:', error);
      return;
    }
    if (cardholderName && termsAgreed && ref.validation && ref.id) {
      const newPaymentInfo = {
        paymentMethod: ref.id,
        displayBrand: ref.card.display_brand,
        expMonth: ref.card.exp_month,
        expYear: ref.card.exp_year,
        funding: ref.card.funding,
        last4: ref.card.last4
      };

      setPaymentInfo(newPaymentInfo);
      await paymentDataF(newPaymentInfo);
    }
  };

  const handleCardValidation = (isValid) => {
    setCreditCardValid(isValid);
    if (!cardholderName) {
      setCreditCardError('Invalid credit card information!');
    } else {
      setCreditCardError('');
    }
  };

  const handleError = (message) => {
    setCreditCardError(message);
  };

  const handleTerm = () => setShowTermsPopup(true);
  const handleBack = async () => {
    try {
      const backBody = {
        TempClientId: tempClientId,
      };
      const backApiData = await api.get(`Client/Back?TempClientId=${tempClientId}`, backBody);
      navigate('/UserSignUp', { state: { ...backApiData.data, isBack: true } });
    } catch (error) {
      console.log('error:', error);
    }
  };

  const handleAgree = () => {
    setTermsAgreed(true);
    setShowTermsPopup(false);
  };

  const handleClose = () => {
    setShowSuccessPopup(false);
    setShowTermsPopup(false);
  };

  const handleLoginPage = () => {
    navigate('/');
  };

  const getClassName = () => {
    if (!paymentInfo.paymentMethod && !termsAgreed) {
      return 'PaymentPage-invalid-info';
    } else {
      return 'PaymentPage-valid-info';
    }
  };

  useEffect(() => {
    if (!tempClientId) {
      navigate('/CompanySignUp');
    }
    setLoaded(true);
    const getIntent = async () => {
      try {
        const raw = await api.get('Client/create-setup-intent');
        const stripeData = raw.data;
        setPaymentInfo((prev) => ({
          ...prev,
          intentId: stripeData.IntentId,
          stripeCustomerId: stripeData.CustomerId,
        }));
        setClientSecret(stripeData.ClientSecret);
        setOptions({ clientSecret: stripeData.ClientSecret, appearance: { theme: "stripe" } });
      } catch (error) {
        console.log('error:', error);
      }
    };

    getIntent();
  }, []);

  if (!loaded) return null;

  return (
    <>
      <Header />
      <SignUpTitle stepNumber={3} />
      <div className="form-container">
        <Form className='form-content' noValidate onSubmit={handleSubmit}>
          <Form.Group controlId="validationCustom01">
            <div className="form-group-inline">
              <Form.Label>Cardholder Name*</Form.Label>
              <div>
                <span className="PaymentPage-text-stripe">stripe</span>
                {submitted && !cardholderName && <div className="invalid-feedback" style={{ display: 'block' }}>required field</div>}
              </div>
            </div>
            <Form.Control
              required
              type="text"
              placeholder="Enter cardholder name"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
              className="step-inputBox"
            />
          </Form.Group>

          <div>
            <div className="form-group-inline">
              <Form.Label>Credit Card*</Form.Label>
              {submitted && !creditCardValid && <div className="invalid-feedback" style={{ display: 'block' }}>{creditCardError}</div>}
            </div>

            {clientSecret && (
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm required name={cardholderName} onCardValidation={handleCardValidation}
                  onError={handleError}
                  ref={checkoutFormRef}
                />
              </Elements>
            )}
          </div>

          {/* Rates part */}
          <RatesTable />  

          <div>
            <Form.Group>
              <Form.Check className="PaymentPage-term-container"
                required
                checked={termsAgreed}
                onChange={(e) => setTermsAgreed(e.target.checked)}

                label={
                  <>
                    <div className="PaymentPage-agree-line">I agree to the
                      <span
                        style={{ textDecoration: "underline", cursor: "pointer", marginLeft: "3px" }}
                        onClick={handleTerm}>Terms of Service</span></div>
                  </>
                }
                feedbackType="invalid"
              />
              {submitted && !termsAgreed && <div className="invalid-feedback" style={{ display: 'block' }}>
                Almost there! Please review and agree to the Terms of Service to finish setting up your account.
              </div>}
            </Form.Group>
          </div>
          <div className="PaymentPage-btn">
            <Buttons
              back
              handleBack={handleBack}
              handleSubmit={handleSubmit}
              text={"Create Account"}
              continueClassName={getClassName()}
            />
          </div>

        </Form>

        {showSuccessPopup && (
          <Popup
            show={showSuccessPopup}
            handleClose={handleLoginPage}
            title={
              <>
                <img src={PopCheck} alt="Verification"
                  style={{ width: "60px", height: "60px", marginBottom: "15px" }}
                />
                <div>Account Created Successfully!</div>
              </>
            }
            titleStyle="popup-title"
            body="Thank you for choosing Voyce as your interpretation service provider."
            bodyStyle="popup-body"
            footer={
              <>
                <div className="paymentpage-success-btn">
                  <Buttons handleSubmit={handleLoginPage} text={"Go to Dashboard"} CardElement='payment-element' /></div>
              </>
            }
            footerStyle="popup-footer"
          />
        )}

        {showTermsPopup && (
          <Popup
            show={showTermsPopup}
            handleClose={handleClose}
            title="Terms of Service"
            titleStyle="popup-title"
            body={
              <>
                <div className="PamentPage-term-text-container">
                  <p>Voyce ....</p>
                  <p>
                    Text abbreviations are shortened forms of words or phrases that
                    help save time and characters. They’re often used for things like
                    social media posts and text messages to limit the number of characters
                    used. Many text abbreviations are also used as slang, especially by younger
                    generations, and keeping up-to-date on the latest text shorthand can help you
                    communicate better with your audience.Text abbreviations are shortened forms of words or phrases that
                    help save time and characters. They’re often used for things like
                    social media posts and text messages to limit the number of characters
                    used. Many text abbreviations are also used as slang, especially by younger
                    generations, and keeping up-to-date on the latest text shorthand can help you
                    communicate better with your audience.Text abbreviations are shortened forms of words or phrases that
                    help save time and characters. They’re often used for things like
                    social media posts and text messages to limit the number of characters
                    used. Many text abbreviations are also used as slang, especially by younger
                    generations, and keeping up-to-date on the latest text shorthand can help you
                    communicate better with your audience.Text abbreviations are shortened forms of words or phrases that
                    help save time and characters. They’re often used for things like
                    social media posts and text messages to limit the number of characters
                    used. Many text abbreviations are also used as slang, especially by younger
                    generations, and keeping up-to-date on the latest text shorthand can help you
                    communicate better with your audience.Text abbreviations are shortened forms of words or phrases that
                    help save time and characters. They’re often used for things like
                    social media posts and text messages to limit the number of characters
                    used. Many text abbreviations are also used as slang, especially by younger
                    generations, and keeping up-to-date on the latest text shorthand can help you
                    communicate better with your audience.
                  </p>
                </div>
              </>
            }
            bodyStyle="popup-body"
            footer={
              <>
                <div className="term-popup-btn">
                  <Buttons back handleBack={handleClose} handleSubmit={handleAgree} text='I Agree' />
                </div>
              </>
            }
            footerStyle="popup-footer"
          />
        )}
      </div>
    </>
  );
};

export default PaymentPage;
